import {
  INFRA_STACK_INDIA,
  INFRA_STACK_UAE,
  infra_stack,
} from '../config/AppConfig';
import * as LC from '../constants/LocaleConstants';

export function getStrapiLocaleFromNextLocale(locale: string): string {
  if (locale === LC.en_BD) return LC.en;
  if (locale === LC.en_AE) return LC.en_AE;
  if (locale === LC.en_TR) return LC.en_TK;
  if (locale === LC.en_IN) return LC.en_IN;
  if (locale === LC.en_MY) return LC.en_MY;
  return locale;
}

export function getNextLocaleFromStrapiLocale(locale: string): string {
  if (locale === LC.en) return LC.en_BD;
  if (locale === LC.en_AE) return LC.en_AE;
  if (locale === LC.en_TK) return LC.en_TR;
  if (locale === LC.en_IN) return LC.en_IN;
  if (locale === LC.en_MY) return LC.en_MY;
  return locale;
}

// BKF-enabled locales, shared by the desktop and mobile booking-flow checks.
// Keep in sync with *.country.ts files.
const BKF_ENABLED_LOCALES = new Set([LC.en_AE, LC.en_IN, LC.en_TR, LC.tr_TR]);

export function enableDesktopBookingFlow(locale?: string): boolean {
  return locale !== undefined && BKF_ENABLED_LOCALES.has(locale);
}

export function enableMobileBookingFlow(locale?: string): boolean {
  return locale !== undefined && BKF_ENABLED_LOCALES.has(locale);
}

export function isUaeStack(): boolean {
  return infra_stack === INFRA_STACK_UAE;
}

export function isIndiaStack(): boolean {
  return infra_stack === INFRA_STACK_INDIA;
}

export function isBkfEnabled(
  locale: string | undefined,
  isDesktop: boolean,
): boolean {
  return (
    (isDesktop && enableDesktopBookingFlow(locale)) ||
    (!isDesktop && enableMobileBookingFlow(locale))
  );
}

export function isPageEnabledForLocaleUae(locale: string): boolean {
  return isUaeStack() && locale === LC.en_AE;
}

export function isPageEnabledForLocaleMalaysia(locale: string): boolean {
  return isUaeStack() && locale === LC.en_MY;
}

export function isMalaysiaLaunchPending(locale: string): boolean {
  return locale === LC.en_MY;
}

export function isPageEnabledForLocaleIndia(locale: string): boolean {
  return isIndiaStack() && locale === LC.en_IN;
}

export function isPageEnabledForLocaleBd(locale: string): boolean {
  return isUaeStack() && (locale === LC.bn_BD || locale === LC.en_BD);
}

export function isPageEnabledForLocaleTurkey(locale: string): boolean {
  return (
    isUaeStack() &&
    (locale === LC.en_TK || locale === LC.en_TR || locale === LC.tr_TR)
  );
}

export function localeToString(locale: string): string {
  if (locale === LC.bn_BD) return LC.bn_string;
  if (locale === LC.en_TR || locale === LC.en_TK || locale === LC.tr_TR)
    return LC.tr_string;
  return LC.en_string;
}

export const SUPPORTED_LOCALES = [
  LC.en_BD,
  LC.bn_BD,
  LC.en_AE,
  LC.en_TR,
  LC.tr_TR,
  LC.en_TK,
  LC.en_IN,
  LC.en_MY,
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export function isSupportedLocale(
  locale: string | undefined,
): locale is SupportedLocale {
  return (
    locale !== undefined &&
    (SUPPORTED_LOCALES as readonly string[]).includes(locale)
  );
}

export function localizedValue<T>(
  locale: string | undefined,
  byLocale: Record<SupportedLocale, T>,
  fallback: T,
): T {
  return isSupportedLocale(locale) ? byLocale[locale] : fallback;
}
